import axios from "axios";

// Utility function to get address from coordinates (reverse geocoding)
export const getAddressFromGeoLocation = async (geoLocation) => {
  if (!geoLocation.latitude || !geoLocation.longitude) {
    throw new Error("Latitude and longitude must be provided for reverse geocoding.");
  }
  try {
    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${geoLocation.latitude}&lon=${geoLocation.longitude}&apiKey=${apiKey}`;
    const res = await axios.get(url, { withCredentials: false, timeout: 5000 });

    if (!res.data.features || res.data.features.length === 0) {
      throw new Error("No address found for this location");
    }

    const feature = res.data.features[0];
    return {
      addressLine: feature.properties.address_line1,
      landmark: feature.properties.suburb || feature.properties.county,
      city: feature.properties.city,
      state: feature.properties.state,
      pincode: feature.properties.postcode,
      country: feature.properties.country,
    };
  } catch (err) {
    throw new Error(`Failed to fetch the address: ${err.message}`);
  }
};

// Utility function to get coordinates from address (forward geocoding)
export const getGeoLocationFromAddress = async (addressData) => {
  if (!addressData || !addressData.city || !addressData.country) {
    throw new Error("Address data must include at least city and country for geocoding.");
  }
  try {
    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;

    // Format the address components into a search query
    const addressQuery = encodeURIComponent(
      [
        addressData.addressLine,
        addressData.landmark,
        addressData.city,
        addressData.state,
        addressData.pincode,
        addressData.country,
      ].filter(Boolean).join(", ")
    );

    const url = `https://api.geoapify.com/v1/geocode/search?text=${addressQuery}&apiKey=${apiKey}`;

    const res = await axios.get(url, { withCredentials: false, timeout: 5000 });

    if (!res.data.features || res.data.features.length === 0) {
      throw new Error("No location found for this address");
    }

    const feature = res.data.features[0];
    return [feature.geometry.coordinates[1], feature.geometry.coordinates[0]];
  } catch (err) {
    throw new Error(`Failed to geocode address: ${err.message}`);
  }
};

// Utility function to get address suggestions (autocomplete)
export const getAddressSuggestions = async (inputText) => {
  if (!inputText || inputText.trim() === "") {
    throw new Error("Input text must be provided for address autocomplete.");
  }
  try {
    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
      inputText
    )}&filter=countrycode:in&apiKey=${apiKey}`;
    const res = await axios.get(url, { withCredentials: false, timeout: 5000 });

    if (!res.data.features || res.data.features.length === 0) {
      throw new Error("No address suggestions found for this input.");
    }

    // Extract and return formatted suggestions
    return res.data.features.map((feature) => ({
      formattedAddress: feature.properties.formatted,
      coordinates: [feature.geometry.coordinates[1], feature.geometry.coordinates[0]], // [latitude, longitude]
      city: feature.properties.city,
      state: feature.properties.state,
      country: feature.properties.country,
    }));
  } catch (err) {
    throw new Error(`Failed to fetch address suggestions: ${err.message}`);
  }
};
